/**
 * MissionChatWithArtifacts
 *
 * Layout: Chat area flanked by an Activity sidebar on the right that
 * mirrors the main navigation sidebar on the left (same bg, border,
 * width, collapse button).
 *
 *   [Sidebar w-72] | [Chat flex-1] | [Activity w-72]
 *
 * The Activity sidebar auto-opens on first execution message and can
 * be collapsed via a hover-reveal chevron button on its left edge. A surface
 * with useActivityPresentation may present it as a sheet instead (a phone),
 * opened only from the header.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PanelRight, ChevronRight, Zap, Pencil } from 'lucide-react'
import { opensActivitySidebar } from '@agion/mission-stream'
import { Button } from '@agion/ui'
import { cn } from '@agion/ui'
import { StreamingCustomerChat } from './StreamingCustomerChat'
import { ExecutionLogPanel } from './ExecutionLogPanel'
import { useChatSurface, type ActivityPresentation, type ActivityRunStatus, type HeaderActionItem } from './chatSurface'
import { useMissionTrust } from '../../hooks/useMissionTrust'
import { useMissionStore } from '../../hooks/useMissions'
import { useAuth } from '../../hooks/useAuth'
import { useMissionCreatePermission } from '../../hooks/useMissionCreatePermission'
import { config } from '../../utils/config'
import { useConversationHistory } from '../../hooks/useConversationHistory'
import type { StreamingChatMessage } from '../../hooks/useStreamingMissionChat'
import type { HeldSubject } from '../../types/escalations'

/** Hold states (Go missionstream OperatorHold*) while a hold waits for or is held by a person. */
const HELD_HOLD_STATES: ReadonlySet<string> = new Set(['waiting', 'joined'])

// ============================================================================
// Props
// ============================================================================

export interface MissionChatWithArtifactsProps {
  missionId: string
  autostart?: boolean
  executionId?: string | null
  hostName?: string
}

// Without a surface that measures, the panel always sits beside the conversation.
function useAsideOnly(): ActivityPresentation {
  return 'aside'
}

// ============================================================================
// Component
// ============================================================================

export const MissionChatWithArtifacts: React.FC<MissionChatWithArtifactsProps> = ({
  missionId,
  autostart = false,
  executionId = null,
  hostName,
}) => {
  const { t } = useTranslation(['chat'])
  const surface = useChatSurface()
  const ActivitySidebar = surface.ActivitySidebar
  // PLAN-20261001 slice 14: a surface that opts in measures this root and may
  // present the Activity panel as a sheet; the current chat measures nothing.
  const rootRef = useRef<HTMLDivElement>(null)
  const measuresPresentation = surface.useActivityPresentation !== undefined
  const useActivityPresentation = surface.useActivityPresentation ?? useAsideOnly
  const presentation = useActivityPresentation(rootRef)
  const [messages, setMessages] = useState<StreamingChatMessage[]>([])
  // The run's state as the conversation knows it; only the new chat's sidebar reads it (agion/Agion#2827).
  const [runStatus, setRunStatus] = useState<ActivityRunStatus | undefined>(undefined)
  const [panelOpen, setPanelOpen] = useState(false)
  const [userClosed, setUserClosed] = useState(false)

  const { trustTier } = useMissionTrust(missionId)
  const mission = useMissionStore(s => s.getMissionById(missionId))
  const { user } = useAuth()
  // The header's run count and other runs: the new chat asks the server for
  // the reader's own and invited runs (F-ACC-03); without the opt-in the read
  // is unchanged.
  const { groupedByMission } = useConversationHistory({ scope: surface.runHistoryScope })

  // Past executions for the expandable header
  const pastExecutions = useMemo(() => {
    const group = groupedByMission.find(g => g.missionId === missionId)
    if (!group) return []
    return group.executions.map(e => ({
      id: e.id,
      timestamp: e.lastActivityAt,
      isActive: !!e.isActive,
    }))
  }, [groupedByMission, missionId])

  // Build participant names map including current user
  const participantNames = useMemo(() => {
    const names: Record<string, string> = { ...mission?.participantNames }
    if (user?.id && user?.name) names[user.id] = user.name
    return names
  }, [mission?.participantNames, user?.id, user?.name])

  // The placement module (@agion/mission-stream) says which kinds open the
  // Activity sidebar.
  const hasExecutionMessages = useMemo(
    () => messages.some(m => opensActivitySidebar(m.type)),
    [messages],
  )

  // Track whether we're actively executing — true only while messages
  // arrive incrementally (live streaming), not on bulk history loads.
  const isTerminated = useMemo(
    () => messages.some(m => m.type === 'mission_completed' || m.type === 'mission_failed'),
    [messages],
  )
  const [isExecuting, setIsExecuting] = useState(false)
  const prevMessageCountRef = useRef(0)

  // True while the latest HITL question in the message history is still
  // open (a hitl_request with no later hitl_response): the run is waiting
  // on a person, not idle and not finished. agion/Agion#2640 — the 10s
  // auto-clear below used to fire during this wait and the Activity panel
  // would read "Run complete" for a run that was genuinely still waiting.
  const isWaitingOnHITL = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (!m) continue
      if (m.type === 'hitl_response') return false
      if (m.type === 'hitl_request') return typeof m.data?.requestId === 'string' && m.data.requestId.length > 0
    }
    return false
  }, [messages])

  // True while the run's latest operator hold (agion/Agion#3435) waits for
  // a person or is held by one: the runner blocks on it as on an open
  // question, and asks none. Without it, an operator who opened the run read
  // the in-flight escalate_to_human call as Failed in the persisted trail.
  const isHeldByPerson = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i]
      if (m?.type !== 'system_notification' || typeof m.data?.holdState !== 'string') continue
      return HELD_HOLD_STATES.has(m.data.holdState)
    }
    return false
  }, [messages])
  // The conversation's governance read of the hold says the signed-in
  // operator holds this run. The operator subscribes after accepting, so the
  // 'joined' notice above has already gone out and history stores none.
  const [isHeldByViewer, setIsHeldByViewer] = useState(false)
  // In operator mode the held run's end user, whom the Activity panel names
  // as the conversation does (agion/Agion#3435).
  const [heldSubject, setHeldSubject] = useState<HeldSubject | undefined>(undefined)
  const isWaitingOnPerson = isWaitingOnHITL || isHeldByPerson || isHeldByViewer

  useEffect(() => {
    if (isTerminated) {
      setIsExecuting(false)
      prevMessageCountRef.current = messages.length
      return
    }
    const prevCount = prevMessageCountRef.current
    const newCount = messages.length
    const delta = newCount - prevCount
    prevMessageCountRef.current = newCount

    // Bulk load (>5 messages at once) = history, not live execution
    if (delta > 5 || delta <= 0 || !hasExecutionMessages) return

    // Incremental messages → actively executing, auto-clear after 10s
    // unless an open HITL question or a held conversation still waits on a
    // person.
    setIsExecuting(true)
    const timer = setTimeout(() => {
      if (!isWaitingOnPerson) setIsExecuting(false)
    }, 10_000)
    return () => clearTimeout(timer)
  }, [messages.length, hasExecutionMessages, isTerminated, isWaitingOnPerson])

  const showsAsExecuting = isExecuting || (isWaitingOnPerson && !isTerminated)

  // A sheet opening on its own mid-run would cover the agent's questions, so
  // in sheet presentation only the header's Open activity opens the panel.
  useEffect(() => {
    if (hasExecutionMessages && !userClosed && !panelOpen && presentation !== 'sheet') {
      setPanelOpen(true)
    }
  }, [hasExecutionMessages, userClosed, panelOpen, presentation])

  const handleMessagesChange = useCallback((msgs: StreamingChatMessage[]) => {
    setMessages(Array.isArray(msgs) ? msgs : [])
  }, [])

  const handleClose = useCallback(() => {
    setPanelOpen(false)
    setUserClosed(true)
  }, [])

  const handleReopen = useCallback(() => {
    setPanelOpen(true)
    setUserClosed(false)
  }, [])

  // Edit in Designer link: on the server's create answer, the same truth as
  // creating a mission (owner, admin, manager and user; an auditor and a guest
  // are refused). Hidden while the answer is being read or unavailable.
  const canDesign = useMissionCreatePermission() === true
  const designerUrl = canDesign && config.adminUrl
    ? `${config.adminUrl}/missions/designer/${missionId}`
    : null

  // Reopen button — shown in chat header when panel is collapsed
  const reopenButton = !panelOpen ? (
    <Button
      variant="ghost"
      size="icon"
      onClick={handleReopen}
      aria-label={t('chat:executionLog.openPanel', 'Open activity')}
      title={t('chat:executionLog.openPanel', 'Open activity')}
    >
      <PanelRight className="w-4 h-4" />
    </Button>
  ) : null

  const headerActions = (
    <>
      {designerUrl && (
        <a
          href={designerUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center h-8 w-8 rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800 transition-colors"
          aria-label={t('chat:missionChat.editInDesigner', 'Edit in Designer')}
          title={t('chat:missionChat.editInDesigner', 'Edit in Designer')}
        >
          <Pencil className="w-4 h-4" />
        </a>
      )}
      {reopenButton}
    </>
  )

  // The same two actions, structured for the next chat surface's header row
  // (agion/Agion issue 2612 item 5) instead of the JSX blob above, which the
  // current chat's own header keeps rendering in its separate row unchanged.
  const headerActionItems: HeaderActionItem[] = [
    ...(designerUrl ? [{ key: 'edit-designer', label: t('chat:missionChat.editInDesigner', 'Edit in Designer'), icon: Pencil, href: designerUrl }] : []),
    ...(!panelOpen ? [{ key: 'open-activity', label: t('chat:executionLog.openPanel', 'Open activity'), icon: PanelRight, onClick: handleReopen }] : []),
  ]

  return (
    <div ref={rootRef} className="flex h-full w-full overflow-hidden">
      {/* Chat area — fills remaining space */}
      <div className="flex-1 min-w-0 overflow-hidden">
        {/* One chat per conversation: the sidebar switches the mission in
            place, and the message box, its staged files and every other
            per-conversation state start afresh instead of carrying over. */}
        <StreamingCustomerChat
          key={missionId}
          missionId={missionId}
          autostart={autostart}
          executionId={executionId}
          onMessagesChange={handleMessagesChange}
          headerActions={headerActions}
          headerActionItems={headerActionItems}
          trustTier={trustTier}
          hostName={hostName}
          pastExecutions={pastExecutions}
          onRunStatusChange={setRunStatus}
          onOperatorHoldChange={setIsHeldByViewer}
          onHeldSubjectChange={setHeldSubject}
        />
      </div>

      {/* The new chat supplies its own sidebar (agion/Agion#2488). */}
      {panelOpen && ActivitySidebar && (
        <ActivitySidebar
          messages={messages}
          isExecuting={showsAsExecuting}
          executionId={executionId}
          agentNameMap={mission?.agentNameMap}
          participantNames={participantNames}
          currentUserName={user?.name}
          orchestratorAgentId={mission?.primaryAgentId || mission?.orchestratorId}
          executionMode={mission?.executionMode}
          missionId={missionId}
          {...(runStatus ? { runStatus } : {})}
          {...(heldSubject ? { heldSubject } : {})}
          onClose={handleClose}
          {...(measuresPresentation ? { presentation } : {})}
        />
      )}

      {/* Activity sidebar — mirrors left navigation sidebar */}
      {panelOpen && !ActivitySidebar && (
        <aside
          className={cn(
            'bg-slate-50 dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800',
            'flex-shrink-0 w-80 flex flex-col transition-all duration-300',
          )}
        >
          {/* Header — same h-16 as the Agion logo header on the left */}
          <div className="group flex items-center justify-between h-16 pl-4 pr-3">
            <span className="flex items-center gap-2">
              <Zap className="h-4 w-4 opacity-30" />
              <span className="text-base font-semibold text-slate-900 dark:text-slate-100">
                {t('chat:executionLog.title', 'Activity')}
              </span>
              {showsAsExecuting && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
              )}
            </span>
            {/* Minimize button — matches left sidebar collapse button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
              aria-label={t('chat:executionLog.closePanel', 'Close activity')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Content — starts at same vertical position as New Chat button */}
          <div className="flex-1 overflow-hidden px-3 pb-3">
            <ExecutionLogPanel
              messages={messages}
              isExecuting={showsAsExecuting}
              executionId={executionId}
              agentNameMap={mission?.agentNameMap}
              participantNames={participantNames}
              currentUserName={user?.name}
              orchestratorAgentId={mission?.primaryAgentId || mission?.orchestratorId}
              {...(heldSubject ? { heldSubject } : {})}
            />
          </div>
        </aside>
      )}
    </div>
  )
}

export default MissionChatWithArtifacts
